"use client";

import { useCallback, type Dispatch, type SetStateAction } from "react";
import { reportProviderClientIssue } from "@/lib/providerClientIssue";
import { pauseSpotifyPlayback, playSpotifyTrackSegment, transferSpotifyPlayback } from "./spotifyWebPlayer.client";
import {
  clampWebPlayerVolume,
  createEnsureReadyResult,
} from "./webPlayerShared";
import {
  errorMessage,
  isMissingSpotifyDeviceResponse,
  spotifySharedStore,
  type SpotifyPlayerErrorOptions,
  type SpotifyPlayerLike,
  type SpotifyPlayerStateResponse,
  type SpotifySharedState,
} from "./spotifyWebPlayer.shared";

type PatchSharedState = (patch: Partial<SpotifySharedState>) => void;

type SetPlayerError = (
  message: string,
  options?: SpotifyPlayerErrorOptions
) => void;

type UseSpotifyPlayerControlsParams = {
  spotifyEnabled: boolean;
  deviceId: string | null;
  getPlayerOrReset: (reason: string) => SpotifyPlayerLike | null;
  patchSharedState: PatchSharedState;
  setVolumeState: Dispatch<SetStateAction<number>>;
  initPlayer: () => void;
  markDeviceUnavailable: () => void;
  resolveTransferDeviceId: (
    preferredDeviceId: string | null
  ) => Promise<string | null>;
  waitForLocalPlaybackActivation: (
    timeoutMs?: number
  ) => Promise<{ active: boolean; isPlaying: boolean }>;
  resumePlaybackOnDevice: (playbackDeviceId: string) => Promise<boolean>;
  fetchRemotePlayerState: () => Promise<SpotifyPlayerStateResponse | null>;
  setPlayerError: SetPlayerError;
};

export function useSpotifyPlayerControls({
  spotifyEnabled,
  deviceId,
  getPlayerOrReset,
  patchSharedState,
  setVolumeState,
  initPlayer,
  markDeviceUnavailable,
  resolveTransferDeviceId,
  waitForLocalPlaybackActivation,
  resumePlaybackOnDevice,
  fetchRemotePlayerState,
  setPlayerError,
}: UseSpotifyPlayerControlsParams) {
  const togglePlay = useCallback(async () => {
    if (!spotifyEnabled) return;
    const player = getPlayerOrReset("togglePlay invalid Spotify player");
    if (!player) return;
    await player.togglePlay();
  }, [getPlayerOrReset, spotifyEnabled]);

  const nextTrack = useCallback(async () => {
    if (!spotifyEnabled) return;
    const player = getPlayerOrReset("nextTrack invalid Spotify player");
    if (!player) return;
    await player.nextTrack();
  }, [getPlayerOrReset, spotifyEnabled]);

  const prevTrack = useCallback(async () => {
    if (!spotifyEnabled) return;
    const player = getPlayerOrReset("prevTrack invalid Spotify player");
    if (!player) return;
    await player.previousTrack();
  }, [getPlayerOrReset, spotifyEnabled]);

  const seek = useCallback(
    async (ms: number) => {
      if (!spotifyEnabled) return;
      const player = getPlayerOrReset("seek invalid Spotify player");
      if (!player) return;
      await player.seek(ms);
    },
    [getPlayerOrReset, spotifyEnabled]
  );

  const setVolume = useCallback(
    async (next: number) => {
      if (!spotifyEnabled) return;
      const clamped = clampWebPlayerVolume(next);
      setVolumeState(clamped);
      patchSharedState({
        volume: clamped,
      });
      const player = getPlayerOrReset("setVolume invalid Spotify player");
      if (!player || typeof player.setVolume !== "function") return;
      try {
        await player.setVolume(clamped);
      } catch {
        // Ignore volume-set failures to keep UI responsive.
      }
    },
    [getPlayerOrReset, patchSharedState, setVolumeState, spotifyEnabled]
  );

  const stopPlayback = useCallback(
    async (resetPosition = true) => {
      if (!spotifyEnabled) return;

      const pauseResult = await pauseSpotifyPlayback();

      const player = getPlayerOrReset("stopPlayback invalid Spotify player");
      if (player && resetPosition) {
        try {
          await player.seek(0);
        } catch {
          // Ignore seek failures after pause so cleanup still completes.
        }
      }

      patchSharedState({
        isPlaying: false,
        positionMs: resetPosition ? 0 : spotifySharedStore.getState().positionMs,
      });

      if (!pauseResult.ok) {
        throw new Error(pauseResult.errorMessage ?? "Failed to pause playback");
      }
    },
    [getPlayerOrReset, patchSharedState, spotifyEnabled]
  );

  const activateElement = useCallback(async () => {
    if (!spotifyEnabled) return;
    const player = getPlayerOrReset("activateElement invalid Spotify player");
    if (!player || typeof player.activateElement !== "function") {
      return;
    }
    await player.activateElement();
  }, [getPlayerOrReset, spotifyEnabled]);

  const ensureReady = useCallback(
    async (timeoutMs = 3000) => {
      if (!spotifyEnabled) {
        return createEnsureReadyResult(false, null, null);
      }
      const sharedState = spotifySharedStore.getState();
      if (sharedState.isReady && sharedState.deviceId) {
        return createEnsureReadyResult(
          true,
          sharedState.deviceId,
          sharedState.error
        );
      }

      let player = getPlayerOrReset("ensureReady invalid Spotify player");
      if (!player && window.Spotify) {
        initPlayer();
        player = getPlayerOrReset("ensureReady invalid Spotify player after init");
      }
      if (!player) {
        const currentState = spotifySharedStore.getState();
        return createEnsureReadyResult(
          false,
          currentState.deviceId,
          currentState.error ?? "Spotify player is unavailable"
        );
      }
      if (typeof player.activateElement === "function") {
        try {
          await player.activateElement();
        } catch {
          // Ignore activation failures; readiness check will handle status.
        }
      }
      if (typeof player.connect === "function") {
        try {
          await player.connect();
        } catch {
          // Ignore connect failures; readiness check will handle status.
        }
      }

      return await new Promise<ReturnType<typeof createEnsureReadyResult>>(
        (resolve) => {
          const start = Date.now();
          const check = () => {
            const currentState = spotifySharedStore.getState();
            if (currentState.isReady && currentState.deviceId) {
              resolve(
                createEnsureReadyResult(
                  true,
                  currentState.deviceId,
                  currentState.error ?? null
                )
              );
              return true;
            }
            if (Date.now() - start >= timeoutMs) {
              resolve(
                createEnsureReadyResult(
                  Boolean(currentState.isReady && currentState.deviceId),
                  currentState.deviceId ?? null,
                  currentState.error ?? null
                )
              );
              return true;
            }
            return false;
          };

          if (check()) return;
          const intervalId = window.setInterval(() => {
            if (check()) {
              window.clearInterval(intervalId);
            }
          }, 120);
        }
      );
    },
    [getPlayerOrReset, initPlayer, spotifyEnabled]
  );

  const resumePlaybackHere = useCallback(
    async (play = false) => {
      if (!spotifyEnabled) return false;
      const player = getPlayerOrReset("resumePlaybackHere invalid Spotify player");
      if (player && typeof player.activateElement === "function") {
        try {
          await player.activateElement();
        } catch {
          // Keep going; transfer/play recovery below can still succeed.
        }
      }
      let playbackDeviceId =
        (await ensureReady(5000)).deviceId ??
        spotifySharedStore.getState().deviceId ??
        deviceId ??
        null;

      if (!playbackDeviceId) {
        playbackDeviceId = await resolveTransferDeviceId(null);
      }

      if (!playbackDeviceId) {
        markDeviceUnavailable();
        return false;
      }

      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const result = await transferSpotifyPlayback(playbackDeviceId, play);

          if (result.ok) {
            patchSharedState({
              deviceId: playbackDeviceId,
              isReady: true,
              needsTransfer: false,
              error: null,
            });
            const localState = await waitForLocalPlaybackActivation(
              play ? 1400 : 800
            );
            if (!play) {
              return (
                localState.active ||
                spotifySharedStore.getState().deviceId === playbackDeviceId
              );
            }

            if (localState.active && localState.isPlaying) {
              return true;
            }

            const remoteState = await fetchRemotePlayerState();
            const remoteDeviceId =
              typeof remoteState?.deviceId === "string"
                ? remoteState.deviceId
                : null;
            const remoteIsPlaying =
              typeof remoteState?.isPlaying === "boolean"
                ? remoteState.isPlaying
                : null;

            if (remoteDeviceId === playbackDeviceId && remoteIsPlaying) {
              return true;
            }

            const resumed = await resumePlaybackOnDevice(playbackDeviceId);
            if (!resumed) {
              return false;
            }

            const resumedState = await waitForLocalPlaybackActivation(2200);
            if (resumedState.active) {
              return true;
            }

            markDeviceUnavailable();
            return false;
          }

          const message = result.errorMessage ?? "Failed to transfer playback";
          if (isMissingSpotifyDeviceResponse(result.status, result.responseText)) {
            markDeviceUnavailable();
            if (attempt === 0) {
              await new Promise((resolve) => window.setTimeout(resolve, 350));
              playbackDeviceId =
                (await ensureReady(5000)).deviceId ??
                (await resolveTransferDeviceId(playbackDeviceId)) ??
                null;
              if (playbackDeviceId) {
                continue;
              }
            }
            return false;
          }

          setPlayerError(message, {
            event: "transfer_playback_failed",
            details: {
              status: result.status,
              play,
              attempt,
              playbackDeviceId,
            },
          });
          return false;
        } catch (error: unknown) {
          setPlayerError(errorMessage(error, "Failed to transfer playback"), {
            event: "transfer_playback_failed",
            error,
            details: {
              play,
              attempt,
              playbackDeviceId,
            },
          });
          return false;
        }
      }

      return false;
    },
    [
      deviceId,
      ensureReady,
      fetchRemotePlayerState,
      getPlayerOrReset,
      markDeviceUnavailable,
      patchSharedState,
      resolveTransferDeviceId,
      resumePlaybackOnDevice,
      setPlayerError,
      spotifyEnabled,
      waitForLocalPlaybackActivation,
    ]
  );

  const playSegment = useCallback(
    async (trackUri: string, startMs: number, durationMs?: number) => {
      if (!spotifyEnabled) return;
      const readyResult = await ensureReady(5000);
      const segmentDeviceId =
        readyResult.deviceId ??
        (await resolveTransferDeviceId(deviceId ?? null)) ??
        deviceId;

      if (!segmentDeviceId) {
        console.warn("[SpotifyWebPlayer] Device not ready");
        reportProviderClientIssue({
          provider: "spotify",
          category: "playback",
          level: "warn",
          event: "segment_device_not_ready",
          title: "Spotify playback device is not ready",
          message:
            "Spotify segment playback could not start because no active device is available.",
          details: {
            trackUri,
            startMs,
            durationMs: durationMs ?? null,
          },
        });
        return;
      }

      try {
        const result = await playSpotifyTrackSegment({
          deviceId: segmentDeviceId,
          trackUri,
          startMs,
        });

        if (!result.ok) {
          console.error("[SpotifyWebPlayer] playSegment failed", result.errorMessage);
          reportProviderClientIssue({
            provider: "spotify",
            category: "playback",
            level: "error",
            event: "segment_playback_failed",
            title: "Spotify segment playback failed",
            message:
              result.errorMessage ?? "Failed to start Spotify segment playback",
            details: {
              status: result.status,
              segmentDeviceId,
              trackUri,
              startMs,
              durationMs: durationMs ?? null,
            },
          });
        }

        if (durationMs && durationMs > 0) {
          window.setTimeout(async () => {
            const pauseResult = await pauseSpotifyPlayback();
            if (!pauseResult.ok) {
              console.warn("[SpotifyWebPlayer] pause after segment failed");
            }
          }, durationMs);
        }
      } catch (error) {
        console.error("[SpotifyWebPlayer] Error playing segment:", error);
        reportProviderClientIssue({
          provider: "spotify",
          category: "playback",
          level: "error",
          event: "segment_playback_failed",
          title: "Spotify segment playback failed",
          message:
            error instanceof Error
              ? error.message
              : "Spotify segment playback failed",
          error,
          details: {
            segmentDeviceId,
            trackUri,
            startMs,
            durationMs: durationMs ?? null,
          },
        });
      }
    },
    [deviceId, ensureReady, resolveTransferDeviceId, spotifyEnabled]
  );

  return {
    togglePlay,
    nextTrack,
    prevTrack,
    seek,
    setVolume,
    stopPlayback,
    activateElement,
    ensureReady,
    resumePlaybackHere,
    playSegment,
  };
}
